import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  TextField,
  Button,
  Grid,
  Typography,
  Paper,
  Box,
  CircularProgress,
  InputAdornment,
  Autocomplete,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { fetchDepartments } from '../../redux/actions/department/departmentAction';
import { fetchEmployees } from '../../redux/actions/employee/employeeAction';
import { fetchPurities } from '../../redux/actions/purity/purityAction';
import { getBatchProcessById, createBatchProcess, updateBatchProcess, getLastProcess, hasStoneProcess } from '../../api/batchInventoryProcess/batchInventoryProcessApi';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { debounce } from 'lodash';
import api from '../../api/axiosConfig';
import SendIcon from '@mui/icons-material/Send';
import FindReplaceIcon from '@mui/icons-material/FindReplace';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { styled } from '@mui/material/styles';
import Alert from '@mui/material/Alert';

// Custom styled TextField for disabled fields
const DisabledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiInputBase-input.Mui-disabled': {
    color: theme.palette.text.primary,
    WebkitTextFillColor: theme.palette.text.primary,
    opacity: 1,
  },
}));

const DepartmentProcessForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { departments } = useSelector((state) => state.department);
  const { employees } = useSelector((state) => state.employee);
  const { purities } = useSelector((state) => state.purity);
  const { user } = useSelector((state) => state.login);

  const [batchNumber, setBatchNumber] = useState('');
  const [inventoryDetails, setInventoryDetails] = useState(null);
  const [toDepartmentOptions, setToDepartmentOptions] = useState([]);
  const [formData, setFormData] = useState({
    from_department_id: '',
    to_department_id: '',
    from_worker_id: '',
    to_worker_id: '',
    issue_weight: '',
    purity_id: '',
    dia_qty: '',
    dia_broken: '',
    color_stone_qty: '',
    color_stone_broken: '',
    nick_plate_qty: '',
    nick_plate_broken: '',
    stone_weight: '',
    stone_qty: '',
    net_weight: '',
    pearl_weight: '',
    pearl_qty: '',
    pearl_weight_cts: '',
    remarks: '',
  });
  const [removalWeight, setRemovalWeight] = useState(0);
  const [isFetching, setIsFetching] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [karatageReadOnly, setKaratageReadOnly] = useState(false);
  const [receivedWeightReadOnly, setReceivedWeightReadOnly] = useState(false);
  const [fromWorkerReadOnly, setFromWorkerReadOnly] = useState(false);
  const [fromDepartmentOptions, setFromDepartmentOptions] = useState(departments);
  const [netWeightAlwaysEnabled, setNetWeightAlwaysEnabled] = useState(false);
  const [toEmployeeEnabled, setToEmployeeEnabled] = useState(false);
  const [lastIsDeptProduction, setLastIsDeptProduction] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    dispatch(fetchDepartments());
    dispatch(fetchEmployees());
    dispatch(fetchPurities());
    if (id) {
      setIsEditMode(true);
      const fetchProcessData = async () => {
        try {
          const response = await getBatchProcessById(id);
          const data = response.data;
          console.log('Edit process data:', data);
          setFormData({
            from_department_id: data.from_department_id,
            to_department_id: data.to_department_id,
            from_worker_id: data.from_worker_id,
            to_worker_id: data.to_worker_id,
            issue_weight: data.issue_weight,
            purity_id: data.purity_id,
            dia_qty: data.dia_qty,
            dia_broken: data.dia_broken,
            color_stone_qty: data.color_stone_qty,
            color_stone_broken: data.color_stone_broken,
            nick_plate_qty: data.nick_plate_qty,
            nick_plate_broken: data.nick_plate_broken,
            stone_weight: data.stone_weight,
            stone_qty: data.stone_qty,
            net_weight: data.net_weight,
            pearl_weight: data.pearl_weight,
            pearl_qty: data.pearl_qty,
            pearl_weight_cts: data.pearl_weight_cts,
            remarks: data.remarks,
          });
          setInventoryDetails({
            id: data.imported_inventory_id,
            product: data.product_code,
            net_weight: data.received_weight,
            hm_purity: data.karatage,
          });
          setBatchNumber(data.batch_number);
          // Enable the switch if to_worker_id is present
          setToEmployeeEnabled(!!data.to_worker_id);
        } catch (error) {
          toast.error('Failed to fetch process data for editing.');
        }
      };
      fetchProcessData();
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (formData.from_department_id) {
        const fromDept = departments.find(d => d.id === formData.from_department_id);
        let availableDepts;
        if (lastIsDeptProduction === 1) {
          // Allow same department as option
          availableDepts = [...departments];
        } else {
          // Exclude same department
          availableDepts = departments.filter(d => d.id !== formData.from_department_id);
        }
        if (fromDept) {
            const nextDeptOrder = fromDept.process_order_no + 1;
            const nextDept = departments.find(d => d.process_order_no === nextDeptOrder);
            if (nextDept && nextDept.id !== fromDept.id) {
                const remainingDepts = availableDepts.filter(d => d.id !== nextDept.id);
                remainingDepts.sort((a,b) => a.process_order_no - b.process_order_no);
                setToDepartmentOptions([nextDept, ...remainingDepts]);
            } else {
                availableDepts.sort((a,b) => a.process_order_no - b.process_order_no);
                setToDepartmentOptions(availableDepts);
            }
        } else {
            availableDepts.sort((a,b) => a.process_order_no - b.process_order_no);
            setToDepartmentOptions(availableDepts);
        }
        // If the previously selected 'To Dept' is now not in the options, clear it.
        if (!availableDepts.find(d => d.id === formData.to_department_id)) {
            setFormData(prev => ({ ...prev, to_department_id: '' }));
        }
    } else {
        const sortedDepts = [...departments].sort((a,b) => a.process_order_no - b.process_order_no);
        setToDepartmentOptions(sortedDepts);
    }
  }, [formData.from_department_id, formData.to_department_id, departments, lastIsDeptProduction]);

  const handleBatchNumberScan = useCallback(async (scannedBatchNumber) => {
    if (!scannedBatchNumber || isEditMode) {
      return;
    }
    setIsFetching(true);
    try {
      const response = await api.get(`/api/batch-inventory-process/inventory/${scannedBatchNumber}`);
      const inventoryData = response.data;
      setInventoryDetails(inventoryData);

      // Check for the last process for this item
      const lastProcessRes = await getLastProcess(inventoryData.id);
      // Check if any previous process had stone columns enabled
      const stoneRes = await hasStoneProcess(inventoryData.id);
      setNetWeightAlwaysEnabled(!!stoneRes.data.hasStone);
      if (lastProcessRes.data) {
        // If a previous process exists
        const lastProcess = lastProcessRes.data;
        setLastIsDeptProduction(lastProcess.is_dept_production);
        const fromDeptId = lastProcess.to_department_id;
        // Reorder departments so fromDeptId is first
        const fromDeptOptions = [
          ...departments.filter(d => d.id === fromDeptId),
          ...departments.filter(d => d.id !== fromDeptId)
        ];
        setFormData(prev => ({
          ...prev,
          from_department_id: fromDeptId,
          purity_id: lastProcess.purity_id,
          from_worker_id: lastProcess.to_worker_id || '', // Only set if previous process has to_worker_id
          issue_weight: '', // Always empty for user entry
        }));
        
        // Calculate received weight including stock issue weight
        const stockIssueWeight = Number(inventoryData.stock_issue_weight) || 0;
        const actualReceivedWeight = Number(lastProcess.issue_weight) || 0;
        const totalReceivedWeight = actualReceivedWeight + stockIssueWeight;
        
        setInventoryDetails(prev => ({
          ...prev,
          net_weight: totalReceivedWeight.toFixed(3) // Received Weight is previous Issue Weight + stock issue weight
        }));
        setKaratageReadOnly(true);
        setReceivedWeightReadOnly(true);
        // Only make from_worker_id readonly if the previous process has a to_worker_id
        setFromWorkerReadOnly(!!lastProcess.to_worker_id);
        setFromDepartmentOptions(fromDeptOptions);
        // --- Net Weight Calculation ---
        const receivedWeight = Number(lastProcess.received_weight) || 0;
        const stoneWeight = Number(lastProcess.stone_weight) || 0;
        const netWeight = receivedWeight - stoneWeight;
        setFormData(prev => ({
          ...prev,
          net_weight: netWeight ? netWeight.toFixed(3) : ''
        }));
      } else {
        // If it's the first time, find the department with the lowest process_order_no
        if (departments.length > 0) {
          const firstDept = departments.reduce((minDept, currentDept) => 
            currentDept.process_order_no < minDept.process_order_no ? currentDept : minDept
          );
          setFormData(prev => ({
            ...prev,
            from_department_id: firstDept.id,
            purity_id: '',
            from_worker_id: '',
            issue_weight: '', // Always empty for user entry
          }));
        }
        // Set received weight from imported inventory including stock issue weight
        const stockIssueWeight = Number(inventoryData.stock_issue_weight) || 0;
        const baseNetWeight = Number(inventoryData.net_weight) || 0;
        const totalNetWeight = baseNetWeight + stockIssueWeight;
        setInventoryDetails(prev => ({ ...prev, net_weight: totalNetWeight.toFixed(3) }));
        setKaratageReadOnly(true); // Make karatage readonly even for first time
        setReceivedWeightReadOnly(true);
        setFromWorkerReadOnly(false);
      }

      // Set purity_id based on hm_purity from inventoryData using backend API
      if (inventoryData && inventoryData.hm_purity) {
        try {
          const purityRes = await api.get(`/api/purity/by-hm-purity/${inventoryData.hm_purity}`);
          if (purityRes.data && purityRes.data.id) {
            setFormData(prev => ({
              ...prev,
              purity_id: purityRes.data.id,
            }));
          } else {
            toast.warn(`No purity found for hm_purity value: ${inventoryData.hm_purity}`);
            setFormData(prev => ({ ...prev, purity_id: '' }));
          }
        } catch (err) {
          toast.warn(`No purity found for hm_purity value: ${inventoryData.hm_purity}`);
          setFormData(prev => ({ ...prev, purity_id: '' }));
        }
      }

      // Set stone-related fields from imported_inventory
      setFormData(prev => ({
        ...prev,
        dia_qty: inventoryData.bom_dia_qty || '',
        stone_weight: inventoryData.stone_weight ? Number(inventoryData.stone_weight).toFixed(3) : '',
        stone_qty: inventoryData.issue_tot_qty || '',
        color_stone_qty: inventoryData.bom_clr_qty || '',
        pearl_weight: inventoryData.stone_weight ? Number(inventoryData.stone_weight).toFixed(3) : '', // Set pearl_weight from stone_weight
        pearl_qty: inventoryData.issue_tot_qty || '',   // Set pearl_qty from issue_tot_qty
      }));

      toast.success('Inventory details fetched successfully.');
    } catch (error) {
      console.error('Error fetching inventory details', error);
      setInventoryDetails(null);
      toast.error(error.response?.data?.message || 'Failed to fetch inventory details.');
    } finally {
      setIsFetching(false);
    }
  }, [isEditMode, departments]);

  const debouncedFetch = useMemo(() => debounce(handleBatchNumberScan, 500), [handleBatchNumberScan]);
  
  useEffect(() => {
      return () => {
          debouncedFetch.cancel();
      }
  }, [debouncedFetch]);

  const handleBatchNumberChange = (e) => {
    const value = e.target.value;
    setBatchNumber(value);
    debouncedFetch(value);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      let newFormData = { ...prev, [name]: value };
      
      // Calculate pearl_weight from pearl_weight_cts if cts is entered
      if (name === 'pearl_weight_cts') {
        if (value && parseFloat(value) > 0) {
          const ctsValue = parseFloat(value);
          const calculatedWeight = (ctsValue * 0.200).toFixed(3);
          newFormData.pearl_weight = calculatedWeight;
        } else {
          // If cts is 0, empty, or invalid, restore original values from imported inventory
          newFormData.pearl_weight = inventoryDetails?.stone_weight ? Number(inventoryDetails.stone_weight).toFixed(3) : '';
          newFormData.pearl_qty = inventoryDetails?.issue_tot_qty || '';
        }
      }
      
      return newFormData;
    });
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    
    // Format weight fields to 3 decimal places only when field loses focus
    const weightFields = ['issue_weight', 'stone_weight', 'pearl_weight', 'net_weight'];
    if (weightFields.includes(name) && value && !isNaN(parseFloat(value))) {
      setFormData(prev => ({
        ...prev,
        [name]: parseFloat(value).toFixed(3)
      }));
    }
  };

  // Get the selected From Dept object
  const selectedFromDept = useMemo(() => {
    return departments.find(d => d.id === formData.from_department_id) || null;
  }, [departments, formData.from_department_id]);

  // Show/hide logic for stone and pearl columns
  const showStonePlateDetails = useMemo(() => {
    return selectedFromDept && selectedFromDept.enable_stone_cols === 1;
  }, [selectedFromDept]);

  const showPearlDetails = useMemo(() => {
    return selectedFromDept && selectedFromDept.enable_pearl_cols === 1;
  }, [selectedFromDept]);

  // Show net weight field after stone setting or pearl fixing processes, or if netWeightAlwaysEnabled is true
  const showNetWeightField = useMemo(() => {
    if (netWeightAlwaysEnabled) return true;
    return selectedFromDept && (selectedFromDept.enable_stone_cols === 1 || selectedFromDept.enable_pearl_cols === 1);
  }, [selectedFromDept, netWeightAlwaysEnabled]);

  // Check if moving to a previous department (reverse movement)
  const isReverseMovement = useMemo(() => {
    if (!selectedFromDept || !formData.to_department_id) return false;
    
    const toDept = departments.find(d => d.id === formData.to_department_id);
    if (!toDept) return false;
    
    // If the target department has a lower process_order_no than the current department, it's a reverse movement
    return toDept.process_order_no < selectedFromDept.process_order_no;
  }, [selectedFromDept, formData.to_department_id, departments]);

  const isAtEndDepartment = selectedFromDept && selectedFromDept.is_end_dept === 1;

  useEffect(() => {
    const issue = parseFloat(formData.issue_weight) || 0;
    const received = parseFloat(inventoryDetails?.net_weight) || 0;
    const stone = parseFloat(formData.stone_weight) || 0;
    const pearl = parseFloat(formData.pearl_weight) || 0;
    const stockIssueWeight = parseFloat(inventoryDetails?.stock_issue_weight) || 0;

    let removal = 0;
    let net = '';
    if (showStonePlateDetails) {
      // New formula for depts with stone details: Removal = Issue - (Received + Stone), Net = Issue - Stone
      removal = issue - (received + stone);
      net = (issue - stone).toFixed(3);
    } else if (showPearlDetails) {
      // New formula for depts with pearl details: Removal = Issue - (Received + Pearl), Net = Issue - (Stone + Pearl)
      removal = issue - (received + pearl);
      net = (issue - (stone + pearl)).toFixed(3);
    } else {
      // Standard formula for other departments
      if (stockIssueWeight > 0) {
        const actualReceivedWeight = received - stockIssueWeight;
        removal = issue - (actualReceivedWeight + stockIssueWeight);
      } else {
        removal = issue - received;
      }
      net = formData.net_weight; // keep as entered or calculated elsewhere
    }

    if (formData.issue_weight) {
      setRemovalWeight(removal.toFixed(3));
      if (showStonePlateDetails || showPearlDetails) {
        setFormData(prev => ({ ...prev, net_weight: net }));
      }
    } else {
      setRemovalWeight(0);
      if (showStonePlateDetails || showPearlDetails) {
        setFormData(prev => ({ ...prev, net_weight: '' }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.issue_weight, formData.stone_weight, formData.pearl_weight, formData.pearl_weight_cts, inventoryDetails, showStonePlateDetails, showPearlDetails]);

  // Calculate net weight based on department type
  useEffect(() => {
    const received = parseFloat(inventoryDetails?.net_weight) || 0;
    const pearl = parseFloat(formData.pearl_weight) || 0;
    
    if (showPearlDetails && formData.pearl_weight) {
      // For pearl processing: net weight = received weight + pearl weight
      const netWeight = received + pearl;
      setFormData(prev => ({
        ...prev,
        net_weight: netWeight.toFixed(3)
      }));
    }
  }, [formData.pearl_weight, inventoryDetails, showPearlDetails]);

  useEffect(() => {
    // If not set by reverse logic, default to all departments
    if (!formData.from_department_id || fromDepartmentOptions.length === 0) {
      setFromDepartmentOptions(departments);
    }
  }, [departments, formData.from_department_id, fromDepartmentOptions.length]);

  // When batch is scanned, reset the switch and to_worker_id
  useEffect(() => {
    if (batchNumber && inventoryDetails && !isEditMode) {
      setToEmployeeEnabled(false);
      setFormData(prev => ({ ...prev, to_worker_id: '' }));
    }
  }, [batchNumber, inventoryDetails, isEditMode]);

  const handleSwitchChange = (e) => {
    setToEmployeeEnabled(e.target.checked);
    if (!e.target.checked) {
      setFormData(prev => ({ ...prev, to_worker_id: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!batchNumber) errors.batchNumber = 'Batch Number is required.';
    if (!inventoryDetails?.product) errors.product = 'Product Code is required.';
    if (!formData.from_department_id) errors.from_department_id = 'From Dept is required.';
    if (!formData.to_department_id) errors.to_department_id = 'To Dept is required.';
    if (
      lastIsDeptProduction === 0 &&
      formData.from_department_id &&
      formData.to_department_id &&
      formData.from_department_id === formData.to_department_id
    ) {
      errors.to_department_id = 'To Dept cannot be the same as From Dept.';
    }
    if (
      (!formData.from_department_id || !formData.to_department_id || formData.from_department_id !== formData.to_department_id) &&
      !formData.from_worker_id
    ) {
      errors.from_worker_id = 'Worker From is required.';
    }
    if (
      toEmployeeEnabled &&
      (!formData.from_department_id || !formData.to_department_id || formData.from_department_id !== formData.to_department_id) &&
      !formData.to_worker_id
    ) {
      errors.to_worker_id = 'To Worker is required.';
    }
    if (formData.from_worker_id && formData.to_worker_id && formData.from_worker_id === formData.to_worker_id) errors.to_worker_id = 'To Worker cannot be the same as From Worker.';
    if (!formData.issue_weight) errors.issue_weight = 'Issue Weight is required.';
    if (!formData.purity_id) errors.purity_id = 'Karatage is required.';
    
    // Make remarks mandatory for reverse movements
    if (isReverseMovement && !formData.remarks?.trim()) {
      errors.remarks = 'Remarks are mandatory when moving to a previous department.';
    }
    
    if (showNetWeightField && !formData.net_weight) errors.net_weight = 'Net Weight is required.';
    if (showStonePlateDetails) {
      // Only require stone_weight and stone_qty
      if (!formData.stone_weight) errors.stone_weight = 'Stone Weight is required.';
      if (!formData.stone_qty) errors.stone_qty = 'Stone Qty is required.';
      // The following fields are NOT required anymore:
      // dia_qty, dia_broken, color_stone_qty, color_stone_broken, nick_plate_qty, nick_plate_broken
      // So, do not add errors for them if empty
    }
    if (showPearlDetails) {
      if (!formData.pearl_weight) errors.pearl_weight = 'Pearl Weight is required.';
      if (!formData.pearl_qty) errors.pearl_qty = 'Pearl Qty is required.';
    }
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validateForm();
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      toast.error('Please fix the errors in the form.');
      return;
    }
    if (!isEditMode && !inventoryDetails) {
      toast.error('Please fetch inventory details by scanning a batch number first.');
      return;
    }

    setIsSaving(true);
    console.log('Submitting with user:', user);

    const processData = {
      ...formData,
      imported_inventory_id: inventoryDetails.id,
      received_weight: inventoryDetails.net_weight,
      created_by: user?.user?.id,
      is_dept_production: toEmployeeEnabled ? 0 : 1,
    };

    // Debug logging
    console.log('Process data being sent:', processData);
    console.log('To department ID:', processData.to_department_id);
    const toDept = departments.find(d => d.id === processData.to_department_id);
    console.log('To department details:', toDept);
    console.log('Is end department:', toDept?.is_end_dept === 1);

    try {
      if (isEditMode) {
        await updateBatchProcess(id, processData);
        toast.success('Inventory process updated successfully!');
        navigate('/transaction/inventory-process-list');
      } else {
        await createBatchProcess(processData);
        toast.success('Inventory process submitted successfully!');
      }
      // Reset form
      setBatchNumber('');
      setInventoryDetails(null);
      setFormData({
        from_department_id: '',
        to_department_id: '',
        from_worker_id: '',
        to_worker_id: '',
        issue_weight: '',
        purity_id: '',
        dia_qty: '',
        dia_broken: '',
        color_stone_qty: '',
        color_stone_broken: '',
        nick_plate_qty: '',
        nick_plate_broken: '',
        stone_weight: '',
        stone_qty: '',
        net_weight: '',
        pearl_weight: '',
        pearl_qty: '',
        pearl_weight_cts: '',
        remarks: '',
      });
      setIsEditMode(false);
      setIsSaving(false);
    } catch (error) {
      console.error('Error submitting process entry', error);
      toast.error(error.response?.data?.message || 'Failed to submit inventory process.');
      setIsSaving(false);
    }
  };

  // Filter employees for Worker From and To Worker based on selected departments
  const filteredFromEmployees = useMemo(() => {
    if (!formData.from_department_id) return employees;
    return employees.filter(e => e.department_id === formData.from_department_id);
  }, [employees, formData.from_department_id]);

  const filteredToEmployees = useMemo(() => {
    if (!formData.to_department_id) return employees;
    return employees.filter(e => e.department_id === formData.to_department_id);
  }, [employees, formData.to_department_id]);

  return (
    <Paper component="form" onSubmit={handleSubmit} sx={{ p: 3 }} autoComplete="off">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
          {isEditMode ? 'Edit Inventory Process' : 'Inventory Process'}
        </Typography>
        <Button
          variant="outlined"
          startIcon={<ListAltIcon />}
          onClick={() => navigate('/transaction/inventory-process-list')}
        >
          View All
        </Button>
      </Box>
      
      <Grid container spacing={3}>
        {/* Left Column */}
        <Grid item xs={12} md={8}>
          <Grid container spacing={3}>
            {/* First Sub-Column */}
            <Grid item xs={12} lg={4}>
              <Box display="flex" flexDirection="column" gap={3}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <DisabledTextField label="Batch Number" value={batchNumber} onChange={handleBatchNumberChange} fullWidth variant="outlined" sx={{ flexGrow: 1 }} disabled={isEditMode || isAtEndDepartment} error={!!formErrors.batchNumber} helperText={formErrors.batchNumber} InputProps={{ endAdornment: (<InputAdornment position="end">{isFetching ? <CircularProgress size={20} /> : <FindReplaceIcon />}</InputAdornment>), }} />
                  <Button onClick={() => handleBatchNumberScan(batchNumber)} variant="outlined" disabled={isFetching || isEditMode || isAtEndDepartment} sx={{ height: '56px', flexShrink: 0 }}>Fetch</Button>
                </Box>
                <DisabledTextField label="Product Code" value={inventoryDetails?.product || ''} fullWidth variant="filled" InputProps={{ readOnly: true }} error={!!formErrors.product} helperText={formErrors.product} />
                <Autocomplete
                  options={fromDepartmentOptions}
                  getOptionLabel={(option) => option.name}
                  value={departments.find(d => d.id === formData.from_department_id) || null}
                  onChange={(event, newValue) => {
                    setFormData({ ...formData, from_department_id: newValue ? newValue.id : '' });
                  }}
                  renderInput={(params) => <DisabledTextField {...params} label="From Dept" variant="outlined" error={!!formErrors.from_department_id} helperText={formErrors.from_department_id} />}
                  disabled
                />
                <Autocomplete options={filteredFromEmployees} getOptionLabel={(option) => `${option.emp_code} - ${option.name}`} value={employees.find(e => e.id === formData.from_worker_id) || null} onChange={(event, newValue) => { setFormData({ ...formData, from_worker_id: newValue ? newValue.id : '' }); }} renderInput={(params) => <DisabledTextField {...params} label="Worker From" variant="outlined" error={!!formErrors.from_worker_id} helperText={formErrors.from_worker_id} />} disabled={fromWorkerReadOnly} />
              </Box>
            </Grid>
            
            {/* Second Sub-Column */}
            <Grid item xs={12} lg={4}>
              <Box display="flex" flexDirection="column" gap={3}>
                <Autocomplete options={toDepartmentOptions} getOptionLabel={(option) => option.name} value={departments.find(d => d.id === formData.to_department_id) || null} onChange={(event, newValue) => { setFormData({ ...formData, to_department_id: newValue ? newValue.id : '' }); }} renderInput={(params) => <DisabledTextField {...params} label="To Dept" variant="outlined" error={!!formErrors.to_department_id} helperText={formErrors.to_department_id} />} />
                <FormControlLabel
                  control={<Switch checked={toEmployeeEnabled} onChange={handleSwitchChange} color="primary" />}
                  label="Move to Department Employee"
                  sx={{ mt: 1 }}
                />
                <Autocomplete options={filteredToEmployees} getOptionLabel={(option) => `${option.emp_code} - ${option.name}`} value={employees.find(e => e.id === formData.to_worker_id) || null} onChange={(event, newValue) => { setFormData({ ...formData, to_worker_id: newValue ? newValue.id : '' }); }} renderInput={(params) => <DisabledTextField {...params} label="To Worker" variant="outlined" error={!!formErrors.to_worker_id} helperText={formErrors.to_worker_id} />} disabled={!toEmployeeEnabled} />
                <DisabledTextField 
                  name="issue_weight" 
                  label="Issue Weight" 
                  value={formData.issue_weight} 
                  onChange={handleChange} 
                  onBlur={handleBlur}
                  fullWidth 
                  type="number" 
                  inputProps={{ step: "0.001" }}
                  variant="outlined" 
                  error={!!formErrors.issue_weight} 
                  helperText={formErrors.issue_weight} 
                />
                <DisabledTextField label="Received Weight" value={inventoryDetails?.net_weight || ''} fullWidth variant={receivedWeightReadOnly ? 'filled' : 'outlined'} InputProps={{ readOnly: receivedWeightReadOnly }} error={!!formErrors.received_weight} helperText={formErrors.received_weight} />
              </Box>
            </Grid>

            {/* Third Sub-Column */}
            <Grid item xs={12} lg={4}>
              <Box display="flex" flexDirection="column" gap={3}>
                <DisabledTextField label="Removal Weight" value={removalWeight} fullWidth variant="filled" InputProps={{ readOnly: true }} />
                <Autocomplete 
                  options={purities} 
                  getOptionLabel={(option) => option.name} 
                  value={purities.find(p => p.id === formData.purity_id) || null} 
                  onChange={(event, newValue) => { setFormData({ ...formData, purity_id: newValue ? newValue.id : '' }); }} 
                  renderInput={(params) => <DisabledTextField {...params} label="Karatage" variant="outlined" error={!!formErrors.purity_id} helperText={formErrors.purity_id} />}
                  disabled={karatageReadOnly}
                />
                {showNetWeightField && (
                  <DisabledTextField 
                    name="net_weight" 
                    label="Net Weight" 
                    value={formData.net_weight} 
                    onChange={handleChange} 
                    onBlur={handleBlur}
                    fullWidth 
                    type="number" 
                    inputProps={{ step: "0.001" }}
                    variant="outlined"
                    error={!!formErrors.net_weight}
                    helperText={formErrors.net_weight}
                  />
                )}
              </Box>
            </Grid>
          </Grid>
          
          {/* Remarks Section */}
          <Grid item xs={12}>
            <Box sx={{ mt: 2 }}>
              {isReverseMovement && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <strong>Reverse Movement Detected:</strong> You are moving to a previous department. Remarks are mandatory for this action.
                </Alert>
              )}
              <TextField
                name="remarks"
                label={`Remarks${isReverseMovement ? ' *' : ''}`}
                value={formData.remarks}
                onChange={handleChange}
                fullWidth
                multiline
                rows={3}
                variant="outlined"
                placeholder={isReverseMovement ? "Remarks are mandatory when moving to a previous department..." : "Enter any additional remarks or notes..."}
                error={!!formErrors.remarks}
                helperText={formErrors.remarks}
                sx={{ 
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: isReverseMovement ? 'warning.main' : 'primary.main',
                    },
                  },
                }}
              />
            </Box>
          </Grid>
        </Grid>

        {/* Right Column (Stone/Plate and/or Pearl Details) */}
        {(showStonePlateDetails || showPearlDetails) && (
          <Grid item xs={12} md={4}>
            {(showStonePlateDetails || showPearlDetails) && (
              <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary', borderBottom: 1, borderColor: 'divider', pb: 1 }}>
                {(showStonePlateDetails && showPearlDetails)
                  ? 'Stone / Plate & Pearl Details'
                  : showStonePlateDetails
                  ? 'Stone / Plate Details'
                  : 'Pearl Details'}
              </Typography>
            )}
            <Box display="flex" flexDirection="column" gap={3}>
              {showStonePlateDetails && (
                <>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <DisabledTextField label="Stone weight" name="stone_weight" value={formData.stone_weight} onChange={handleChange} onBlur={handleBlur} type="number" inputProps={{ step: "0.001" }} fullWidth />
                    <DisabledTextField label="Stone qty" name="stone_qty" value={formData.stone_qty} onChange={handleChange} type="number" fullWidth />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <DisabledTextField label="Dia Qty" name="dia_qty" value={formData.dia_qty} onChange={handleChange} type="number" fullWidth />
                    <DisabledTextField label="Broken" name="dia_broken" value={formData.dia_broken} onChange={handleChange} type="number" fullWidth />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <DisabledTextField label="Color Stone Qty" name="color_stone_qty" value={formData.color_stone_qty} onChange={handleChange} type="number" fullWidth />
                    <DisabledTextField label="Broken" name="color_stone_broken" value={formData.color_stone_broken} onChange={handleChange} type="number" fullWidth />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <DisabledTextField label="Nick Plate Qty" name="nick_plate_qty" value={formData.nick_plate_qty} onChange={handleChange} type="number" fullWidth />
                    <DisabledTextField label="Broken" name="nick_plate_broken" value={formData.nick_plate_broken} onChange={handleChange} type="number" fullWidth />
                  </Box>
                </>
              )}
              {showPearlDetails && (
                <>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <DisabledTextField label="Pearl Weight (gms)" name="pearl_weight" value={formData.pearl_weight} onChange={handleChange} onBlur={handleBlur} type="number" inputProps={{ step: "0.001" }} fullWidth InputProps={{ readOnly: !!formData.pearl_weight_cts }} />
                    <DisabledTextField label="Pearl Qty" name="pearl_qty" value={formData.pearl_qty} onChange={handleChange} type="number" fullWidth />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <DisabledTextField 
                      label="Pearl Weight (cts)" 
                      name="pearl_weight_cts" 
                      value={formData.pearl_weight_cts} 
                      onChange={handleChange} 
                      onBlur={handleBlur}
                      type="number" 
                      inputProps={{ step: "0.001" }}
                      fullWidth 
                      sx={{
                        '& .MuiInputLabel-root': {
                          color: '#2196f3',
                          fontWeight: 'bold',
                        },
                      }}
                    />
                  </Box>
                </>
              )}
            </Box>
          </Grid>
        )}
      </Grid>
      
      {isAtEndDepartment && (
        <Alert severity="warning" sx={{ mt: 4, mb: 2, fontWeight: 'bold', fontSize: 16, justifyContent: 'center', alignItems: 'center', textAlign: 'center' }}>
          <strong>The order is completed.</strong><br/>
          You cannot move the order to any other department.<br/>
          Please contact admin.
        </Alert>
      )}
      
      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          color="primary" 
          size="large"
          startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
          disabled={isAtEndDepartment || isSaving}
        >
          {isSaving ? (isEditMode ? 'Updating...' : 'Saving...') : (isEditMode ? 'Update' : 'Save')}
        </Button>
      </Box>
    </Paper>
  );
};

export default DepartmentProcessForm;